import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  LocalOffer as CouponIcon,
  Campaign as AdsIcon,
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  FlashOn as FlashOnIcon,
  AccountBalanceWallet as WalletIcon,
  PendingActions as PendingActionsIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  PhotoCamera as PhotoCameraIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { hasFranchiseeOperatorPermission } from "../../config/franchiseeOperatorPermissions";
import { getApiBaseUrl } from "../../services/httpClient";

const API_BASE_URL = getApiBaseUrl();

const StatCard = ({ title, value, icon, color, chipLabel, onClick }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      border: "1px solid",
      borderColor: `${color}.main`,
      background: `linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0))`,
      height: "100%",
      position: "relative",
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-4px)",
            boxShadow: 4,
          }
        : {},
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            color: "common.white",
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        {chipLabel && (
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
            label={chipLabel}
            size="small"
            color={color}
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
        {value}
      </Typography>
      {onClick && (
        <Button
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{ mt: 2, fontWeight: 600, textTransform: "none", px: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          View details
        </Button>
      )}
    </CardContent>
  </Card>
);

const QuickActionButton = ({ title, description, icon, onClick }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      px: 3,
      py: 2.5,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1,
      borderColor: "divider",
      transition: "all 0.3s ease",
      cursor: onClick ? "pointer" : "default",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: 3,
        transform: "translateY(-4px)",
      },
    }}
    onClick={onClick}
  >
    <Avatar
      sx={{
        bgcolor: "primary.light",
        color: "primary.main",
        width: 44,
        height: 44,
      }}
    >
      {icon}
    </Avatar>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
      {description}
    </Typography>
    <Button
      size="small"
      endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
      sx={{ alignSelf: "flex-start", px: 0 }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      Go to module
    </Button>
  </Card>
);

const FranchiseeDashboard = () => {
  const { user, hasFranchiseFeature } = useAuth();
  const navigate = useNavigate();
  const isFranchiseeOperator = user?.role === "franchisee_operator";
  const isBusinessShopRole =
    user?.role === "business_admin" || user?.role === "shop_admin";
  const operatorPermissions = Array.isArray(user?.permissions)
    ? user.permissions
    : [];
  const hasOp = (key) =>
    hasFranchiseeOperatorPermission(operatorPermissions, key);
  const canBill =
    isFranchiseeOperator
      ? hasOp("bill_access")
      : isBusinessShopRole ||
        hasFranchiseFeature("smartbill") ||
        hasFranchiseFeature("cart_model_bill") ||
        hasFranchiseFeature("view_bills");
  const canCoupons = isFranchiseeOperator
    ? hasOp("coupon_management")
    : isBusinessShopRole || hasFranchiseFeature("coupon_management");
  // Citizen redeem → shop inbox (same gates as sidebar /shop-orders).
  const canShopOrders = canBill || canCoupons;
  // Loksy membership policy: Ads are available to every enrolled Vyapar owner,
  // including the Free plan. Delegated operators still need an explicit grant.
  const canAds = isFranchiseeOperator ? hasOp("ads_management") : true;
  const canPendingWorks = isFranchiseeOperator
    ? hasOp("pending_works")
    : hasFranchiseFeature("smartbill");
  const canEnquiries = isFranchiseeOperator
    ? hasOp("enquiries")
    : hasFranchiseFeature("smartbill");
  // View Bills / customer search unlock with any bill-creation module.
  const canCustomerSearch = isFranchiseeOperator
    ? hasOp("customer_search")
    : hasFranchiseFeature("view_bills");
  const canCounterStats = isFranchiseeOperator
    ? hasOp("counter_stats")
    : hasFranchiseFeature("counter_stats");
  const canReports = false;
  const displayBusinessName =
    user?.business_name ||
    user?.full_name ||
    user?.contact_person_name ||
    user?.email?.split("@")?.[0] ||
    (isFranchiseeOperator ? "Operator" : "Business Admin");

  const statsUserKey =
    user?.userId || user?.id || user?.phone || "anonymous";

  const {
    data: stats,
    isLoading,
    error,
  } = useQuery(
    ["franchisee-dashboard-stats", statsUserKey, user?.role],
    async () => {
      const token =
        localStorage.getItem("admin_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("admin_token") ||
        sessionStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/dashboard/franchisee-stats`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        },
      );
      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Failed to load dashboard statistics",
        );
      }
      return response.data.data || response.data;
    },
    {
      enabled: Boolean(user?.userId || user?.id),
      staleTime: 0,
      cacheTime: 60 * 1000,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      retry: 2,
    },
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load dashboard statistics. Please refresh the page or log in
          again.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    canBill
      ? {
          title: "Total Bills",
          value: stats?.bills?.total ?? 0,
          icon: <ReceiptIcon />,
          color: "primary",
          chip: stats?.bills?.today ? `${stats.bills.today} today` : null,
          onClick: () => navigate("/bills/view"),
        }
      : null,
    canCoupons
      ? {
          title: "Active Coupons",
          value: stats?.coupons?.active ?? 0,
          icon: <CouponIcon />,
          color: "warning",
          chip: stats?.coupons?.total ? `${stats.coupons.total} total` : null,
          onClick: () => navigate("/coupons"),
        }
      : null,
    canAds
      ? {
          title: "Published Ads",
          value: stats?.ads?.active ?? 0,
          icon: <AdsIcon />,
          color: "success",
          chip: stats?.ads?.total ? `${stats.ads.total} total` : null,
          onClick: () => navigate("/locsy/ads"),
        }
      : null,
    canReports || canBill
      ? {
          title: "Total Revenue",
          value: `₹${stats?.revenue?.total ?? 0}`,
          icon: <WalletIcon />,
          color: "info",
          chip: stats?.revenue?.today ? `₹${stats.revenue.today} today` : null,
        }
      : null,
  ].filter(Boolean);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {error && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Some statistics may not be available. Showing cached data.
        </Alert>
      )}
      {/* Welcome Header */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 3,
          p: 4,
          overflow: "hidden",
          background:
            "linear-gradient(120deg, #059669 0%, #10b981 50%, #34d399 100%)",
          color: "common.white",
          minHeight: { xs: 200, md: 220 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.12,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 200C0 89.5431 89.5431 0 200 0C310.457 0 400 89.5431 400 200C400 310.457 310.457 400 200 400C89.5431 400 0 310.457 0 200Z' fill='white'/%3E%3C/svg%3E\")",
          }}
        />
        <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.8 }}>
          Hello {displayBusinessName}
        </Typography>
        <Typography variant="h4" fontWeight={700}>
          LOCSY Business Dashboard
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 520, opacity: 0.85 }}>
          Access the enabled modules for your business/service operations.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {canBill && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<FlashOnIcon />}
              sx={{
                borderRadius: 3,
                px: 3,
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={() => navigate("/bills/create")}
            >
              Create New Bill
            </Button>
          )}
          {canCoupons && (
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: 3,
                px: 3,
                fontWeight: 600,
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.6)",
                color: "common.white",
              }}
              onClick={() => navigate("/coupons")}
            >
              Redeem Coupons
            </Button>
          )}
          {canShopOrders && (
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: 3,
                px: 3,
                fontWeight: 600,
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.6)",
                color: "common.white",
              }}
              onClick={() => navigate("/shop-orders")}
            >
              Shop Orders
            </Button>
          )}
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...card} chipLabel={card.chip} />
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 3,
              height: "100%",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              Quick Actions
            </Typography>
            <Grid container spacing={2.5}>
              {canBill && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Create Bill"
                    description="Generate bills with QR codes and smart features."
                    icon={<ReceiptIcon />}
                    onClick={() => navigate("/bills/create")}
                  />
                </Grid>
              )}
              {canCoupons && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Redeem Coupons"
                    description="Create, manage and redeem discount coupons for customers."
                    icon={<CouponIcon />}
                    onClick={() => navigate("/coupons")}
                  />
                </Grid>
              )}
              {canShopOrders && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Shop Orders"
                    description="Citizen redeem coupon orders — review, quote and collect payment."
                    icon={<ReceiptLongIcon />}
                    onClick={() => navigate("/shop-orders")}
                  />
                </Grid>
              )}
              {canAds && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Publish Ads"
                    description="Create and publish local advertisements for your business."
                    icon={<AdsIcon />}
                    onClick={() => navigate("/locsy/ads")}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <QuickActionButton
                  title="Products"
                  description="Manage product groups, catalog, stock and bill taxes."
                  icon={<StoreIcon />}
                  onClick={() => navigate("/locsy/products")}
                />
              </Grid>
              {!isFranchiseeOperator && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Accounts"
                    description="Purchase stock, track expenses and follow up customer dues."
                    icon={<WalletIcon />}
                    onClick={() => navigate("/locsy/accounts")}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <QuickActionButton
                  title="My Shop Photos"
                  description="Update your profile photo, logo and shop photos for the Loksy app."
                  icon={<PhotoCameraIcon />}
                  onClick={() => navigate("/locsy/shop-photos")}
                />
              </Grid>
              {canBill && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="View Bills"
                    description="View and manage all your bills and transactions."
                    icon={<ReceiptIcon />}
                    onClick={() => navigate("/bills/view")}
                  />
                </Grid>
              )}
              {canBill && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Advance Bills"
                    description="View advance amount bills (count in sales after Completed)."
                    icon={<WalletIcon />}
                    onClick={() => navigate("/bills/advance-bills")}
                  />
                </Grid>
              )}
              {canPendingWorks && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Pending Works"
                    description="Track and update pending work items."
                    icon={<PendingActionsIcon />}
                    onClick={() => navigate("/bills/pending-works")}
                  />
                </Grid>
              )}
              {canEnquiries && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Enquiries"
                    description="Register enquiry — call / WhatsApp when product or service is available."
                    icon={<QuestionAnswerIcon />}
                    onClick={() => navigate("/bills/enquiry-form")}
                  />
                </Grid>
              )}
              {canCustomerSearch && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Customer Search"
                    description="Search customers and open their dashboard."
                    icon={<SearchIcon />}
                    onClick={() => navigate("/customer/search")}
                  />
                </Grid>
              )}
              {canCounterStats && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Counter Stats"
                    description="View counter sales and service charge statistics."
                    icon={<AssessmentIcon />}
                    onClick={() => navigate("/bills/service-charges")}
                  />
                </Grid>
              )}
              {!isFranchiseeOperator && hasFranchiseFeature("job_alerts") && (
                <Grid item xs={12} sm={6}>
                  <QuickActionButton
                    title="Job alerts"
                    description="Create and manage job alerts for your area."
                    icon={<StoreIcon />}
                    onClick={() => navigate("/job-alerts")}
                  />
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              p: 3,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Today's Summary
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {[
                canBill
                  ? {
                      label: "Bills Created",
                      value: stats?.bills?.today ?? 0,
                      accent: "primary.main",
                    }
                  : null,
                canBill
                  ? {
                      label: "Revenue Today",
                      value: `₹${stats?.revenue?.today ?? 0}`,
                      accent: "success.main",
                    }
                  : null,
                canCoupons
                  ? {
                      label: "Active Coupons",
                      value: stats?.coupons?.active ?? 0,
                      accent: "warning.main",
                    }
                  : null,
              ]
                .filter(Boolean)
                .map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      border: "1px dashed",
                      borderColor: item.accent,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {item.label}
                    </Typography>
                    <Chip
                      label={item.value}
                      sx={{
                        fontWeight: 600,
                        bgcolor: `${item.accent}15`,
                        color: item.accent,
                      }}
                    />
                  </Box>
                ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FranchiseeDashboard;
